import { ReactNode, useState } from 'react';
import {
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CenteredSpinner } from '~/commonComponents/CenteredSpinner';

const SAVE_ERROR_MESSAGE = 'Something went wrong. Please try again.';

interface ISectionEditDialogProps {
  /** e.g. "Edit Basic Information". */
  title: string;
  /** Prefix for the dialog's handles: `<dataCy>-dialog`, `-save`, `-cancel`, `-error`. */
  dataCy: string;
  /** Cancel, the close button, Escape and the backdrop all discard the edits. */
  onClose: () => void;
  /**
   * Runs the section's save. A rejection keeps the dialog open with what was
   * typed and shows the save error; the dialog owns that state.
   */
  onSave: () => Promise<unknown>;
  canSave: boolean;
  /** Shows a spinner instead of the form while the section's data loads. */
  isLoading?: boolean;
  children?: ReactNode;
}

/**
 * The shell every Location Details section and Personal Information edit in
 * (Figma 7532-66348, 7235-92248). It is mounted only while open, so closing it
 * drops the form state — that is what "Cancel discards changes" rests on.
 */
export const SectionEditDialog = ({
  title,
  dataCy,
  onClose,
  onSave,
  canSave,
  isLoading = false,
  children,
}: ISectionEditDialogProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const save = async () => {
    setHasSaveError(false);
    setIsSaving(true);
    try {
      await onSave();
    } catch {
      setHasSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} size="medium" aria-label={title} data-cy={`${dataCy}-dialog`}>
      <DialogTitle onClose={onClose} closeLabel="Close">
        <Typography variant="h6" color={colors.text.primary}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <Column gap={6}>
            {children}
            {hasSaveError && (
              <Typography variant="body2" color={colors.error[500]} data-cy={`${dataCy}-error`}>
                {SAVE_ERROR_MESSAGE}
              </Typography>
            )}
          </Column>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onClose}
          data-cy={`${dataCy}-cancel`}
        >
          Cancel
        </Button>
        <Button
          size="small"
          onClick={save}
          loading={isSaving}
          disabled={isLoading || !canSave || isSaving}
          data-cy={`${dataCy}-save`}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
